import React, { useEffect, useState } from "react";
import { Modal } from "rsuite";
import { MdCheck } from "react-icons/md";
import { toast } from "react-toastify";
import api from "../../../../services/axios";
import { upperCaseFirstLetter } from "../../../../utils/formatStringUtils";

type TeamModel = {
  employeeId: number;
  projectId: number;
  clientId: number;
  flActive: boolean;
};

const selectClass = (disabled: boolean) =>
  `w-full rounded-xl border px-3 py-2.5 text-sm transition-all
  ${
    disabled
      ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400 dark:border-gray-700 dark:bg-navy-900 dark:text-gray-500"
      : "border-gray-200 bg-white text-gray-800 focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white"
  }`;

interface StepBadgeProps {
  number: number;
  active: boolean;
  done: boolean;
}

const StepBadge: React.FC<StepBadgeProps> = ({ number, active, done }) => (
  <span
    aria-hidden="true"
    className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-colors
      ${done ? "bg-emerald-500 text-white" : active ? "bg-dv-gradient text-white shadow-dv-glow" : "bg-gray-200 text-gray-600 dark:bg-navy-700 dark:text-gray-400"}`}
  >
    {done ? <MdCheck className="h-4 w-4" /> : number}
  </span>
);

const ModalAddTeam: React.FC<any> = ({
  openAddTeamModal,
  setOpenAddTeamModal,
  updateState,
}) => {
  const [loading, setLoading] = useState(false);
  const [allProjects, setAllProjects] = useState<any[]>([]);
  const [clientData, setClientData] = useState<any[]>([]);
  const [employeeData, setEmployeeData] = useState<any[]>([]);

  const [selectedE, setSelectedE] = useState<number>(0);
  const [selectedC, setSelectedC] = useState<number>(0);
  const [selectedP, setSelectedP] = useState<number>(0);

  const handleClose = () => {
    setSelectedE(0);
    setSelectedC(0);
    setSelectedP(0);
    setOpenAddTeamModal(false);
  };

  const handleClientChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedC(parseInt(event.target.value));
    setSelectedP(0);
  };

  const handleRegisterTeam = async () => {
    const teamData: TeamModel = {
      employeeId: selectedE,
      projectId: selectedP,
      clientId: selectedC,
      flActive: true,
    };
    try {
      setLoading(true);
      await api.post("/EmployeeClientProject", teamData);
      toast.success("Membro adicionado à equipe com sucesso!", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      updateState();
      handleClose();
    } catch (error) {
      console.error("Error adding team member:", error);
      toast.error("Não foi possível adicionar o membro. Tente novamente.", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    Promise.all([
      api.get("/Project/Projects"),
      api.get("/Client/Clients"),
      api.get("/Employee/employees"),
    ])
      .then(([projectsRes, clientsRes, employeesRes]) => {
        // Selects de vínculo só listam ativos (projetos ativos, clientes/colaboradores ativos).
        setAllProjects((projectsRes.data ?? []).filter((p: any) => p.status == null || p.status === 1));
        setClientData((clientsRes.data ?? []).filter((c: any) => c.flActive));
        setEmployeeData((employeesRes.data ?? []).filter((e: any) => e.flActive));
      })
      .catch((error) => {
        console.error("Error loading data:", error);
        toast.error("Não foi possível carregar os dados do formulário.", { position: "top-right", autoClose: 3000 });
      });
  }, []);

  const canConfirm = selectedE !== 0 && selectedC !== 0 && selectedP !== 0;

  return (
    <Modal backdrop="static" open={openAddTeamModal} size="md" onClose={handleClose}>
      <Modal.Header>
        <Modal.Title>
          <span className="text-lg font-bold text-navy-700 dark:text-white">
            Adicionar membro à equipe
          </span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="space-y-6 py-2">
          {/* Step 1 — Employee */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <StepBadge number={1} active={true} done={selectedE !== 0} />
              <label
                htmlFor="team-employee"
                className="text-sm font-semibold text-gray-700 dark:text-gray-300"
              >
                Colaborador
              </label>
            </div>
            <select
              id="team-employee"
              className={selectClass(false)}
              value={selectedE}
              onChange={(e) => setSelectedE(parseInt(e.target.value))}
            >
              <option value={0}>Selecione um colaborador...</option>
              {employeeData.map((e: any) => (
                <option key={e.id} value={e.id}>
                  {upperCaseFirstLetter(e.nmEmployee || e.userName || e.email)}
                </option>
              ))}
            </select>
          </div>

          {/* Step 2 — Client */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <StepBadge number={2} active={selectedE !== 0} done={selectedC !== 0} />
              <label
                htmlFor="team-client"
                className={`text-sm font-semibold transition-colors ${
                  selectedE !== 0
                    ? "text-gray-700 dark:text-gray-300"
                    : "text-gray-400 dark:text-gray-500"
                }`}
              >
                Cliente
              </label>
            </div>
            <select
              id="team-client"
              className={selectClass(selectedE === 0)}
              value={selectedC}
              onChange={handleClientChange}
              disabled={selectedE === 0}
            >
              <option value={0}>Selecione um cliente...</option>
              {clientData.map((c: any) => (
                <option key={c.id} value={c.id}>
                  {c.legalName}
                </option>
              ))}
            </select>
          </div>

          {/* Step 3 — Project */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <StepBadge number={3} active={selectedC !== 0} done={selectedP !== 0} />
              <label
                htmlFor="team-project"
                className={`text-sm font-semibold transition-colors ${
                  selectedC !== 0
                    ? "text-gray-700 dark:text-gray-300"
                    : "text-gray-400 dark:text-gray-500"
                }`}
              >
                Projeto
              </label>
            </div>
            <select
              id="team-project"
              className={selectClass(selectedC === 0)}
              value={selectedP}
              onChange={(e) => setSelectedP(parseInt(e.target.value))}
              disabled={selectedC === 0}
            >
              <option value={0}>Selecione um projeto...</option>
              {allProjects.map((p: any) => (
                <option key={p.id} value={p.id}>
                  {p.projectName}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={handleClose}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/15 dark:text-gray-200 dark:hover:bg-navy-700"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleRegisterTeam}
            disabled={!canConfirm || loading}
            className="dv-sheen flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-dv-gradient px-5 py-2.5 text-sm font-bold text-white shadow-dv-glow transition-all active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? "Adicionando..." : "Confirmar"}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalAddTeam;
